import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslateService } from '@ngx-translate/core';
import { finalize, forkJoin, Subject, switchMap, takeUntil } from 'rxjs';
import { IndustryTypeLookup } from 'src/app/model/ai-agent';
import { resolveSignupIndustryId } from 'src/app/modules/ai-studio/agentix-create/utils/industry-type.util';
import { XADropdownConfig } from 'src/app/model/xa-dropdown-model';
import { CommonService } from 'src/app/services/common/common.service';
import { environment } from 'src/environments/environment';
import { AgentixTrialCountryOption, AgentixTrialSignupFlowService } from './services/agentix-trial-signup-flow.service';

/** Dial code option for the phone country code dropdown. */
export interface AgentixTrialCountryCodeOption {
  code: string;
  regExpression?: string;
  mobileNumberFormat?: string;
  urlFlag?: string;
}

export type AgentixTrialSignupStep = 'email' | 'verifyOtp' | 'verified' | 'profile' | 'confirmation';

const OTP_LENGTH = 4;
const RESEND_SECONDS = 60;

@Component({
  selector: 'app-agentix-trial-signup',
  templateUrl: './agentix-trial-signup.component.html',
  styleUrls: [ './agentix-trial-signup.component.scss' ],
  standalone: false
})
export class AgentixTrialSignupComponent implements OnInit, OnDestroy {
  currentStep: AgentixTrialSignupStep = 'email';
  emailForm: FormGroup;
  profileForm: FormGroup;
  email = '';
  otpValue = '';
  otpError = '';
  resendCounter = RESEND_SECONDS;
  isSubmitting = false;
  isLoadingLookups = false;

  industryOptions: IndustryTypeLookup[] = [];
  countryOptions: AgentixTrialCountryOption[] = [];
  countryCodeOptions: AgentixTrialCountryCodeOption[] = [];

  readonly otpConfig = {
    length: OTP_LENGTH,
    allowNumbersOnly: true,
    disableAutoFocus: false,
    inputClass: 'agentix-trial-otp-input'
  };

  readonly industryConfig: XADropdownConfig = {
    name: 'industryId',
    label: 'agentix_trial_signup_industry_label',
    labelKey: 'name',
    valueKey: 'id',
    showRequiredLabel: true,
    enableSearch: false
  };

  readonly countryConfig: XADropdownConfig = {
    name: 'country',
    label: 'country',
    labelKey: 'name',
    valueKey: 'id',
    showRequiredLabel: true,
    enableSearch: true
  };

  readonly countryCodeConfig: XADropdownConfig = {
    name: 'code',
    label: 'code',
    placeHolder: 'code',
    labelKey: 'code',
    valueKey: 'code',
    enableSearch: true,
    showHeaderIcon: true
  };

  private resendTimerId: ReturnType<typeof setInterval> | null = null;
  private readonly destroy$ = new Subject<void>();
  phonePlaceholder: string = 'mobile_number';

  /**
   * Constructor
   */
  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly msalService: MsalService,
    private readonly commonService: CommonService,
    private readonly translateService: TranslateService,
    private readonly flowService: AgentixTrialSignupFlowService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.commonService.dismissBootstrapLoader();
  }

  /**
   * Initialize forms and hide global loading overlay.
   */
  ngOnInit(): void {
    this.commonService.dismissBootstrapLoader();
    this.commonService.hideLoading();
    this.initializeForms();
    this.bootstrapLookups();
  }

  /**
   * Clear resend timer on destroy.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearResendTimer();
    this.commonService.hideLoading();
  }

  /**
   * Active marketing slide index for the left panel.
   */
  get activeMarketingSlide(): number {
    const slideMap: Record<AgentixTrialSignupStep, number> = {
      email: 0,
      verifyOtp: 1,
      verified: 2,
      profile: 3,
      confirmation: 4
    };
    return slideMap[this.currentStep];
  }

  /**
   * Masked email for OTP and confirmation screens.
   */
  get maskedEmail(): string {
    return this.maskEmail(this.email);
  }

  /**
   * Formatted resend countdown as mm:ss.
   */
  get formattedResendCounter(): string {
    const minutes = Math.floor(this.resendCounter / 60);
    const seconds = this.resendCounter % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  /**
   * Whether the email step submit button should be disabled.
   */
  get disableEmailSubmit(): boolean {
    return this.emailForm.invalid || this.isSubmitting;
  }

  /**
   * Whether the OTP verify button should be disabled.
   */
  get disableOtpSubmit(): boolean {
    return this.otpValue.length !== OTP_LENGTH || this.isSubmitting;
  }

  /**
   * Whether the profile save button should be disabled.
   */
  get disableProfileSubmit(): boolean {
    return this.profileForm.invalid || this.isSubmitting || this.isLoadingLookups;
  }

  /**
   * Send verification code and advance to OTP step.
   */
  sendCode(): void {
    if (this.disableEmailSubmit) {
      this.emailForm.markAllAsTouched();
      return;
    }

    this.email = (this.emailForm.get('email')?.value || '').trim();
    this.isSubmitting = true;
    this.commonService.showLoading();
    this.flowService
      .bootstrapGuestToken()
      .pipe(
        switchMap(() => this.flowService.sendCode(this.email)),
        finalize(() => {
          this.isSubmitting = false;
          this.commonService.hideLoading();
          this.cdr.markForCheck();
        })
      )
      .subscribe((success) => {
        if (success) {
          this.currentStep = 'verifyOtp';
          this.resetOtpState();
          this.startResendCountdown();
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * Verify the six-digit OTP and advance to verified step.
   */
  verifyOtp(): void {
    if (this.disableOtpSubmit) {
      return;
    }

    this.isSubmitting = true;
    this.otpError = '';
    this.commonService.showLoading();
    this.flowService
      .verifyCode(this.email, this.otpValue)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.commonService.hideLoading();
          this.cdr.markForCheck();
        })
      )
      .subscribe((result) => {
        if (result.isValid) {
          this.currentStep = 'verified';
          this.clearResendTimer();
        } else {
          this.otpError = result.error || this.translateService.instant('free_trial_invalid_otp_code');
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * Continue from verified screen to profile step.
   */
  continueToProfile(): void {
    this.currentStep = 'profile';
  }

  /**
   * Save profile and advance to confirmation.
   */
  saveProfile(): void {
    if (this.disableProfileSubmit) {
      this.profileForm.markAllAsTouched();
      return;
    }

    const countryId = this.profileForm.get('country')?.value;
    const countryRow = this.countryOptions.find(row => row.id === countryId);
    const selectedIndustryId = this.profileForm.get('industryId')?.value;
    const countryCode = this.profileForm.get('countryCode')?.value || '';
    const phoneNumber = (this.profileForm.get('phoneNumber')?.value || '').trim();
    const payload = {
      email: this.email,
      name: this.profileForm.get('fullName')?.value?.trim(),
      organization: this.profileForm.get('organization')?.value?.trim(),
      industryId: resolveSignupIndustryId(this.industryOptions, selectedIndustryId),
      country: countryRow?.name || `${countryId ?? ''}`,
      phone: `${countryCode} ${phoneNumber}`
    };

    this.isSubmitting = true;
    this.commonService.showLoading();
    this.flowService
      .saveProfile(payload)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.commonService.hideLoading();
          this.cdr.markForCheck();
        })
      )
      .subscribe((success) => {
        if (success) {
          this.currentStep = 'confirmation';
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * Navigate to AI Studio after signup completion.
   */
  getStarted(): void {
    this.signIn();
  }

  /**
   * Redirect existing users to sign in and land in AI Studio after authentication.
   */
  signIn(): void {
    this.msalService.loginRedirect({
      redirectStartPage: '/ai-studio',
      scopes: [ environment.b2cSettings.caseManagementApiScope ]
    });
  }

  /**
   * Redirect users back to the Addenda marketing website.
   */
  goBack(): void {
    window.history.back();
  }

  /**
   * Return to the email signup step.
   */
  backToSignup(): void {
    this.currentStep = 'email';
    this.resetOtpState();
    this.clearResendTimer();
  }

  /**
   * Change email from verified step.
   */
  changeEmail(): void {
    this.backToSignup();
  }

  /**
   * Resend verification code and restart countdown.
   */
  resendCode(): void {
    if (this.resendCounter > 0) {
      return;
    }

    this.commonService.showLoading();
    this.flowService
      .bootstrapGuestToken()
      .pipe(
        switchMap(() => this.flowService.resendCode(this.email)),
        finalize(() => this.commonService.hideLoading())
      )
      .subscribe((success) => {
        if (success) {
          this.resetOtpState();
          this.startResendCountdown();
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * Handle OTP input changes.
   * @param value - Current OTP value from ng-otp-input
   */
  onOtpChange(value: string): void {
    this.otpValue = value || '';
  }

  /**
   * Resolve field-level validation message for a form control.
   * @param form - Parent form group
   * @param controlName - Control name
   */
  getFieldError(form: FormGroup, controlName: string): string {
    const control = form.get(controlName);
    if (!control || !control.touched || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.translateService.instant('field_required');
    }

    if (control.errors['email']) {
      return this.translateService.instant('email_valid');
    }

    if (control.errors['pattern']) {
      return this.translateService.instant('enter_valid_mobile_number');
    }

    return '';
  }

  /**
   * Initialize reactive forms for email and profile steps.
   */
  private initializeForms(): void {
    this.emailForm = this.formBuilder.group({
      email: [ '', [ Validators.required, Validators.email ] ]
    });

    this.profileForm = this.formBuilder.group({
      fullName: [ '', [ Validators.required ] ],
      organization: [ '', [ Validators.required ] ],
      industryId: [ null, [ Validators.required ] ],
      country: [ null, [ Validators.required ] ],
      countryCode: [ null ],
      phoneNumber: [ '' ]
    });

    this.profileForm.get('countryCode').valueChanges.subscribe((val) => {
      const selectedCode = this.countryCodeOptions.find(x => x.code === val);
      this.profileForm.get('phoneNumber').setValue(null);
      this.profileForm.get('phoneNumber').setValidators([ Validators.pattern(selectedCode.regExpression) ]);
      this.profileForm.get('phoneNumber').updateValueAndValidity();
      this.phonePlaceholder = selectedCode.mobileNumberFormat;
      this.cdr.markForCheck();
    });

    // Default the dial code to the selected country's; the user can still pick another code.
    this.profileForm.get('country').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((countryId) => {
      const phoneCountryCode = this.countryOptions.find(row => row.id === countryId)?.phoneCountryCode;
      const countryCodeControl = this.profileForm.get('countryCode');
      if (phoneCountryCode && countryCodeControl.value !== phoneCountryCode) {
        countryCodeControl.setValue(phoneCountryCode);
      }
    });
  }

  /**
   * Bootstrap guest token and load industry/country lookups for the profile step.
   */
  private bootstrapLookups(): void {
    this.isLoadingLookups = true;
    this.flowService
      .bootstrapGuestToken()
      .pipe(
        switchMap(() =>
          forkJoin({
            industries: this.flowService.loadIndustryTypes(),
            countries: this.flowService.loadCountries()
          })
        ),
        finalize(() => {
          this.isLoadingLookups = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(({ industries, countries }) => {
        this.industryOptions = industries;
        this.countryOptions = countries;
        this.countryCodeOptions = this.buildCountryCodeOptions(countries);
        this.cdr.markForCheck();
      });
  }

  /**
   * Build a unique, sorted list of dial codes from the country lookup.
   * When several countries share a code, the first row's pattern and format are kept.
   * @param countries - Country rows from the lookup API
   * @returns Dial code options for the country code dropdown
   */
  private buildCountryCodeOptions(countries: AgentixTrialCountryOption[]): AgentixTrialCountryCodeOption[] {
    const byCode = new Map<string, AgentixTrialCountryCodeOption>();
    countries.forEach((row) => {
      if (row.phoneCountryCode && !byCode.has(row.phoneCountryCode)) {
        byCode.set(row.phoneCountryCode, {
          code: row.phoneCountryCode,
          regExpression: row.regExpression,
          mobileNumberFormat: row.mobileNumberFormat,
          urlFlag: row.urlFlag
        });
      }
    });
    return Array.from(byCode.values()).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  }

  /**
   * Mask an email address for display.
   * @param value - Raw email address
   */
  private maskEmail(value: string): string {
    const trimmed = value?.trim();
    if (!trimmed || !trimmed.includes('@')) {
      return trimmed || '';
    }

    const [ localPart, domain ] = trimmed.split('@');
    if (localPart.length <= 2) {
      return `${localPart[0] || ''}***@${domain}`;
    }

    return `${localPart[0]}***${localPart[localPart.length - 1]}@${domain}`;
  }

  /**
   * Reset OTP input state.
   */
  private resetOtpState(): void {
    this.otpValue = '';
    this.otpError = '';
  }

  /**
   * Start the resend countdown timer.
   */
  private startResendCountdown(): void {
    this.clearResendTimer();
    this.resendCounter = RESEND_SECONDS;
    this.resendTimerId = setInterval(() => {
      if (this.resendCounter > 0) {
        this.resendCounter--;
        this.cdr.markForCheck();
        return;
      }
      this.clearResendTimer();
      this.cdr.markForCheck();
    }, 1000);
  }

  /**
   * Clear the resend countdown timer.
   */
  private clearResendTimer(): void {
    if (this.resendTimerId) {
      clearInterval(this.resendTimerId);
      this.resendTimerId = null;
    }
  }
}
